import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ConfirmModal, Icon, showError, Text } from '@feathr/components';
import { useToggle } from '@feathr/hooks';

export interface ICopyFromVersionAButtonProps {
  disabled: boolean;
  hasUnsavedChanges: boolean;
  onCopy: () => Promise<void>;
}

function CopyFromVersionAButton({
  disabled,
  hasUnsavedChanges,
  onCopy,
}: Readonly<ICopyFromVersionAButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const [isModalOpen, toggleModal] = useToggle(false);
  const [isCopying, setIsCopying] = useState(false);

  async function handleConfirm(): Promise<void> {
    setIsCopying(true);
    try {
      await onCopy();
    } catch (error) {
      showError(error, t);
    } finally {
      setIsCopying(false);
      toggleModal();
    }
  }

  function handleClose(): void {
    if (!isCopying) {
      toggleModal();
    }
  }

  return (
    <>
      <Button
        disabled={disabled || hasUnsavedChanges || isCopying}
        onClick={toggleModal}
        prefix={<Icon icon={faCopy} />}
        tooltip={hasUnsavedChanges ? t('Save your changes to copy Version A.') : undefined}
      >
        {t('Copy from Version A')}
      </Button>
      <ConfirmModal
        confirmButtonText={t('Copy')}
        confirmLoading={isCopying}
        onClose={handleClose}
        onConfirm={handleConfirm}
        opened={isModalOpen}
        t={t}
        title={t('Copy from Version A?')}
      >
        <Text.DefaultRegular truncate={false}>
          {t(
            "Copy all fields from Version A to Version B? This will overwrite any content you've entered for Version B.",
          )}
        </Text.DefaultRegular>
      </ConfirmModal>
    </>
  );
}

export default observer(CopyFromVersionAButton);
